/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  ColorSwatchPicker,
  ControlledInput,
  ControlledTextarea,
  FormInput,
  FormUpload,
  PrimaryButton,
  RedButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { useUploadImage } from "../../store/useUploadImage";
import { useAuth } from "../../store/useAuth";
import { useAlert } from "../../store/useAlert";
import { IPartner, usePartners } from "../../store/usePartners";
import { IVoucher, useVouchers } from "../../store/useVouchers";
import { VouchersTable } from "./VouchersTable";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { Switch } from "@material-tailwind/react";

type Inputs = {
  name: string;
  en_description: string;
  nl_description: string;
  website: string;
};

function EditPartnerScreen() {
  /** Refs */
  const fileInputRef = useRef(null);
  /** Params */
  const params = useParams();
  /** Hooks */
  const {
    getAllVouchers,
    allVouchers,
    hasNextPage,
    addVoucher,
    addManyVoucher,
    updateVoucher,
  } = useVouchers();
  const {
    updatePartner,
    updatedPartner,
    clearUpdatedPartner,
    getOnePartner,
    onePartner,
  } = usePartners();
  const navigate = useNavigate();
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  const {
    uploadImage,
    setUploadedImage,
    setUploadedThumbnail,
    clearUploadedImage,
    uploadedImage,
    thumbnailImage,
  } = useUploadImage();
  const { setAlert } = useAlert();
  const currentUser = useAuth((state) => state.user);
  const canSetCourier = [1, 7].includes(Number(currentUser?.user_role ?? 0));
  /** State */
  const [image, setImage] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [showInApp, setShowInApp] = useState<boolean>(false);
  const [isCourier, setIsCourier] = useState<boolean>(false);
  const [order, setOrder] = useState<string>("0");
  // Voucher State
  const [vouchersPage, setVouchersPage] = useState<number>(1);
  const [modal, setModal] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<boolean>(false);
  const [editingVoucher, setEditingVoucher] = useState<IVoucher | null>(null);
  const [showBulkDataModal, setShowBulkDataModal] = useState<boolean>(false);
  const [voucherCode, setVoucherCode] = useState<string>("");
  const [voucherValue, setVoucherValue] = useState<string>("");
  const [voucherDescription, setVoucherDescription] = useState<string>("");
  const [voucherInstructions, setVoucherInstructions] = useState<string>("");
  const [voucherLink, setVoucherLink] = useState<string>("");
  const [voucherBenefitText, setVoucherBenefitText] = useState<string>("");
  const [voucherColor, setVoucherColor] = useState<string>("#FF5726");
  const [voucherExpireDate, setVoucherExpireDate] = useState<string>("");
  const [voucherSourceType, setVoucherSourceType] = useState<
    "CHALLENGE" | "LOTTERY"
  >("CHALLENGE");
  const [allowMultipleRedeems, setAllowMultipleRedeems] =
    useState<boolean>(false);
  const [voucherSubmitted, setVoucherSubmitted] = useState(false);
  const [isVoucherSaving, setIsVoucherSaving] = useState(false);
  const [uploadedDataVouchers, setUploadedDataVouchers] = useState<
    {
      code: string;
      value?: string;
      description?: string;
      instructions?: string;
      benefitText?: string;
      expireDate?: string;
      partnerId: string;
      sourceType?: "CHALLENGE" | "LOTTERY" | "UNKNOWN";
    }[]
  >([]);

  /** Effects */
  useEffect(() => {
    if (params?.id) {
      getOnePartner(params?.id as string);
      getAllVouchers(1, 10, params?.id as string);
    }
  }, [params?.id]);

  useEffect(() => {
    if (uploadedDataVouchers.length) {
      setUploadedDataVouchers((prev) =>
        prev.map((voucher) => ({
          ...voucher,
          sourceType: voucherSourceType,
        }))
      );
    }
  }, [voucherSourceType, uploadedDataVouchers.length]);

  useEffect(() => {
    if (updatedPartner) {
      clearUpdatedPartner();
      clearUploadedImage();
      navigate(-1);
    }
  }, [updatedPartner]);

  useEffect(() => {
    if (onePartner?._id) {
      reset({
        name: onePartner.name,
        en_description: onePartner.en_description,
        nl_description: onePartner.nl_description,
        website: onePartner.website,
      });
      setShowInApp(onePartner.showInApp ?? false);
      setIsCourier(onePartner.isCourier ?? false);
      setOrder(String(onePartner.order ?? 0));
      if (onePartner?.image) {
        setUploadedImage(onePartner.image);
      }
      if (onePartner?.thumbnail) {
        setUploadedThumbnail(onePartner.thumbnail);
      }
    }
  }, [onePartner]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  const processData = (data: any[], partnerId: string) => {
    const onlyData = data?.slice(1);
    let processedData: any = [];
    for (let data of onlyData) {
      if (data?.length > 1) {
        processedData.push({
          code: data[0],
          value: data[1] || undefined,
          description: data[2] || undefined,
          instructions: data[3] || undefined,
          benefitText: data[4] || undefined,
          expireDate: data[5] || undefined,
          partnerId: partnerId,
          sourceType: voucherSourceType,
        });
      }
    }

    setUploadedDataVouchers(processedData);
    setShowBulkDataModal(true);
  };

  const handleFileUpload = (event: any) => {
    const file = event.target.files[0];

    if (file) {
      // Check file extension
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      if (fileExtension === "csv") {
        handleCSV(file);
      } else if (fileExtension === "xlsx" || fileExtension === "xls") {
        handleExcel(file);
      } else {
        console.error("Unsupported file format");
      }
    }
  };

  const handleCSV = (file: File) => {
    return Papa.parse(file, {
      complete: (result: any) => {
        processData(result.data, params?.id as string);
      },
    });
  };

  const handleExcel = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // Process each sheet
        const allData: any[] = [];
        workbook.SheetNames.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const csvData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          allData.push(...csvData);
        });

        processData(allData, params?.id as string);
      } catch (error) {
        console.error("Error processing Excel file:", error);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const resetVoucherFormFields = () => {
    setVoucherCode("");
    setVoucherValue("");
    setVoucherDescription("");
    setVoucherInstructions("");
    setVoucherLink("");
    setVoucherBenefitText("");
    setVoucherColor("#FF5726");
    setVoucherExpireDate("");
    setVoucherSourceType("CHALLENGE");
    setAllowMultipleRedeems(false);
    setVoucherSubmitted(false);
    setEditingVoucher(null);
  };

  const closeVoucherModal = () => {
    resetVoucherFormFields();
    setModal(false);
  };

  const closeEditVoucherModal = () => {
    resetVoucherFormFields();
    setEditModal(false);
  };

  const handleEditVoucher = (voucher: IVoucher) => {
    setEditingVoucher(voucher);
    setVoucherCode(voucher.code || "");
    setVoucherValue(voucher.value || "");
    setVoucherDescription(voucher.description || "");
    setVoucherInstructions(voucher.instructions || "");
    setVoucherLink(voucher.link || "");
    setVoucherBenefitText(voucher.benefitText || "");
    setVoucherColor(voucher.color || "#FF5726");
    setVoucherExpireDate(
      voucher.expireDate
        ? new Date(voucher.expireDate).toISOString().split("T")[0]
        : ""
    );
    setVoucherSourceType(
      voucher.sourceType === "LOTTERY" ? "LOTTERY" : "CHALLENGE"
    );
    // Check both field names (allowMultipleRedeems and allowMultipleRedemptions)
    const isMultipleRedeems =
      voucher.allowMultipleRedeems ||
      voucher.allowMultipleRedemptions === true ||
      voucher.allowMultipleRedemptions === "true";
    setAllowMultipleRedeems(isMultipleRedeems || false);
    setEditModal(true);
  };

  const addNewVoucher = async () => {
    setVoucherSubmitted(true);
    if (!voucherCode.trim()) {
      return setAlert({
        isOpen: true,
        message: "Please fill in all required fields.",
        severity: "error",
      });
    }
    const body = {
      code: voucherCode.trim(),
      value: voucherValue || undefined,
      description: voucherDescription || undefined,
      instructions: voucherInstructions || undefined,
      link: voucherLink || undefined,
      benefitText: voucherBenefitText || undefined,
      color: voucherColor,
      expireDate: voucherExpireDate || undefined,
      partnerId: params?.id as string,
      sourceType: voucherSourceType,
      allowMultipleRedemptions: allowMultipleRedeems,
    };
    try {
      setIsVoucherSaving(true);
      await addVoucher(body);
      closeVoucherModal();
      getAllVouchers(1, 10, params?.id as string);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ??
        "We couldn't create the voucher right now. Please try again.";
      setAlert({
        isOpen: true,
        message,
        severity: "error",
      });
    } finally {
      setIsVoucherSaving(false);
    }
  };

  const updateExistingVoucher = async () => {
    if (!editingVoucher?._id) return;
    setVoucherSubmitted(true);
    if (!voucherCode.trim()) {
      return setAlert({
        isOpen: true,
        message: "Please fill in all required fields.",
        severity: "error",
      });
    }
    const body = {
      code: voucherCode.trim(),
      value: voucherValue || undefined,
      // Sent even when empty: undefined drops the key from the request, and
      // the backend then keeps the old text, so a cleared field came back.
      description: voucherDescription ?? "",
      instructions: voucherInstructions ?? "",
      link: voucherLink ?? "",
      benefitText: voucherBenefitText ?? "",
      color: voucherColor,
      expireDate: voucherExpireDate || undefined,
      partnerId: params?.id as string,
      sourceType: voucherSourceType,
      allowMultipleRedemptions: allowMultipleRedeems,
    };
    try {
      setIsVoucherSaving(true);
      await updateVoucher(editingVoucher._id, body);
      closeEditVoucherModal();
      getAllVouchers(vouchersPage, 10, params?.id as string);
      setAlert({
        isOpen: true,
        message: "Voucher updated successfully.",
        severity: "success",
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.message ??
        "We couldn't update the voucher right now. Please try again.";
      setAlert({
        isOpen: true,
        message,
        severity: "error",
      });
    } finally {
      setIsVoucherSaving(false);
    }
  };

  const onSubmit = async (data: Inputs) => {
    if (!uploadedImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    let body: IPartner = {
      ...data,
      image: uploadedImage,
      // For a carrier the backend only takes this from an admin, the same
      // roles that see its switch below.
      showInApp,
      order: Number(order) || 0,
    };
    // Only sent by a role allowed to set it — see canSetCourier.
    if (canSetCourier) {
      body.isCourier = isCourier;
    }
    if (thumbnailImage) {
      body.thumbnail = thumbnailImage;
    }
    updatePartner(params?.id as string, body);
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  useEffect(() => {
    if (image) {
      onUploadImage();
    }
  }, [image]);

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image, true);
  };

  const imageError = submitted && !image;

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Edit partner
        </h1>
      </div>
      <div className="flex flex-col sm:flex-col">
        <div className="w-full sm:w-1/2 bg-white  border-gray-border  rounded-xl drop-shadow-none mb-2 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Partner
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            <ControlledInput
              control={control}
              error={Boolean(errors?.name)}
              label="Name"
              placeholder="Partner name"
              name="name"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.website)}
              label="Website"
              placeholder="Partner website"
              name="website"
              rules={{
                required: true,
                pattern: /https?:\/\/.+/,
              }}
            />
            <ControlledTextarea
              name="en_description"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.en_description)}
              label="Description (EN)"
              placeholder="Type partner description in English language here"
              inputClassName="min-h-[180px]"
            />
            <ControlledTextarea
              name="nl_description"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.nl_description)}
              label="Description (NL)"
              placeholder="Type partner description in Dutch language here"
              inputClassName="min-h-[180px]"
            />
            <FormUpload
              label="Partner logo"
              onChange={onImageChange}
              value={image}
              error={imageError}
              imageUrl={uploadedImage as string}
            />
            <FormInput
              label="Display order"
              placeholder="0"
              type="number"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            />
            {isCourier && !canSetCourier ? (
              <p className="text-xs text-gray-600">
                {onePartner?.showInApp && onePartner?.active !== false
                  ? "This carrier is shown in the app's partner list."
                  : "This carrier is not shown in the app's partner list."}{" "}
                Only an admin can change that.
              </p>
            ) : (
              <Switch
                crossOrigin={"anonymous"}
                color="teal"
                ripple
                label={
                  isCourier
                    ? "Show this carrier in mobile app"
                    : "Show this partner in mobile app"
                }
                checked={showInApp}
                onChange={(e) => setShowInApp(e?.target.checked)}
              />
            )}
            {isCourier && canSetCourier ? (
              // A deactivated carrier is dropped from the app and the Partners
              // list and cannot take parcels, whatever the switch says.
              onePartner?.active === false ? (
                <p className="text-xs text-red-700">
                  This carrier is deactivated, so the app does not list it and
                  it cannot take parcels, whatever this switch says.
                </p>
              ) : (
                <p className="text-xs text-gray-600">
                  Off by default. When on, the carrier is listed with the other
                  partners in the app, and under Partners (Visible in app),
                  where you can drag it into position. Parcel registration works
                  either way.
                </p>
              )
            ) : null}
            {/* The only place the kind can still be changed, and only for a
                partner that already is a carrier: an ordinary partner is
                created and edited without ever meeting this switch. */}
            {canSetCourier && (onePartner?.isCourier || isCourier) ? (
              <Switch
                crossOrigin={"anonymous"}
                color="teal"
                ripple
                label="This partner is a parcel carrier"
                checked={isCourier}
                onChange={(e) => setIsCourier(e?.target.checked)}
              />
            ) : null}
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
        {/* Vouchers */}
        <div className="w-full sm:mt-2 bg-white rounded-xl border-gray-border drop-shadow-none mb-2 sm:mb-0">
          <div className="w-full flex flex-row items-center justify-between border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Vouchers
            </p>
            <div className="flex flex-row items-center">
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
              />
              {/* <PrimaryButton
                label="Add"
                size="sm"
                onClick={() => setModal(true)}
              />
              <PrimaryButton
                label="Add many"
                onClick={openFileUpload}
                size="sm"
                className="ml-2"
              /> */}
            </div>
          </div>
          <div className="w-full px-7 py-7">
            {!allVouchers?.length ? (
              <p className="text-center font-satoshi text-base text-dark">
                There is no Voucher created yet.
              </p>
            ) : (
              <VouchersTable onEditVoucher={handleEditVoucher} />
            )}
            {hasNextPage ? (
              <div className="flex flex-row w-full items-center justify-end mt-3">
                <PrimaryButton
                  onClick={() => {
                    setVouchersPage(vouchersPage + 1);
                    getAllVouchers(vouchersPage + 1, 10, params?.id as string);
                  }}
                  label="Load more"
                  className="h-10"
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {modal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-10/12 sm:w-1/4 my-0 sm:my-6 mx-auto max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className="flex items-start justify-center p-5 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-3xl font-semibold font-satoshi text-dark">
                    Add new Voucher
                  </h3>
                </div>
                {/*body*/}
                <div className="relative p-6 flex-auto">
                  <FormInput
                    label="Code *"
                    placeholder="Voucher code"
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value)}
                  />
                  {voucherCode === "" && voucherSubmitted ? (
                    <p className="font-satoshi text-red-600 font-medium">
                      Code is required*
                    </p>
                  ) : null}

                  <FormInput
                    label="Value"
                    placeholder="e.g., (2500 for €25), 20%, Free coffee, Gift set"
                    value={voucherValue}
                    onChange={(e) => setVoucherValue(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    A plain number is read as cents: 500 shows as &euro;5.00 and
                    5 as &euro;0.05. Write &euro;5, 20% or any text to have it
                    shown exactly as typed. Decimals are not supported.
                  </p>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Voucher description"
                      value={voucherDescription}
                      onChange={(e) => setVoucherDescription(e.target.value)}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Instructions
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="e.g., Please come to our Amsterdam store to pick up your gift set"
                      value={voucherInstructions}
                      onChange={(e) => setVoucherInstructions(e.target.value)}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="url"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., https://www.partner.nl/actie"
                      value={voucherLink}
                      onChange={(e) => setVoucherLink(e.target.value)}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Shown as one tappable line at the bottom of the voucher.
                    </p>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Benefit Text
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      placeholder="e.g., on all items, on coffee only, on selected products"
                      value={voucherBenefitText}
                      onChange={(e) => setVoucherBenefitText(e.target.value)}
                    />
                  </div>
                  <div className="mt-4">
                    <ColorSwatchPicker
                      value={voucherColor}
                      onChange={setVoucherColor}
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expire Date
                    </label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={voucherExpireDate}
                      onChange={(e) => setVoucherExpireDate(e.target.value)}
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Voucher usage
                    </label>
                    <select
                      value={voucherSourceType}
                      onChange={(e) =>
                        setVoucherSourceType(
                          e.target.value === "LOTTERY" ? "LOTTERY" : "CHALLENGE"
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="CHALLENGE">Challenge reward</option>
                      <option value="LOTTERY">Lottery reward</option>
                    </select>
                    {/* Says what this actually creates. Vouchers made here
                        belong to the partner, not to one challenge — which is
                        what lets a challenge be configured before any voucher
                        is earmarked to it. */}
                    <p className="mt-2 text-xs text-gray-600">
                      {voucherSourceType === "CHALLENGE"
                        ? "Added to this partner's pool. Any of the partner's challenges can pay out with it — create these before the challenge."
                        : "Lottery prizes are assigned to one specific lottery from the Lottery vouchers page, so create the lottery first."}
                    </p>
                  </div>
                  <div className="mt-4">
                    <Switch
                      crossOrigin={"anonymous"}
                      color="teal"
                      ripple
                      label="Allow multiple redeems"
                      checked={allowMultipleRedeems}
                      onChange={(e) =>
                        setAllowMultipleRedeems(e?.target.checked)
                      }
                    />
                  </div>
                </div>
                {/*footer*/}
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <RedButton
                    label="Cancel"
                    className="mr-4"
                    onClick={closeVoucherModal}
                  />
                  <PrimaryButton
                    disabled={!voucherCode.trim() || isVoucherSaving}
                    label={isVoucherSaving ? "Saving..." : "Add"}
                    onClick={addNewVoucher}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}

      {editModal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-10/12 sm:w-1/4 my-0 sm:my-6 mx-auto max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className="flex items-start justify-center p-5 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-3xl font-semibold font-satoshi text-dark">
                    Edit Voucher
                  </h3>
                </div>
                {/*body*/}
                <div className="relative p-6 flex-auto">
                  <FormInput
                    label="Code *"
                    placeholder="Voucher code"
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value)}
                  />
                  {voucherCode === "" && voucherSubmitted ? (
                    <p className="font-satoshi text-red-600 font-medium">
                      Code is required*
                    </p>
                  ) : null}

                  <FormInput
                    label="Value"
                    placeholder="e.g., (2500 for €25), 20%, Free coffee, Gift set"
                    value={voucherValue}
                    onChange={(e) => setVoucherValue(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    A plain number is read as cents: 500 shows as &euro;5.00 and
                    5 as &euro;0.05. Write &euro;5, 20% or any text to have it
                    shown exactly as typed. Decimals are not supported.
                  </p>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Voucher description"
                      value={voucherDescription}
                      onChange={(e) => setVoucherDescription(e.target.value)}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Instructions
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="e.g., Please come to our Amsterdam store to pick up your gift set"
                      value={voucherInstructions}
                      onChange={(e) => setVoucherInstructions(e.target.value)}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="url"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., https://www.partner.nl/actie"
                      value={voucherLink}
                      onChange={(e) => setVoucherLink(e.target.value)}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Shown as one tappable line at the bottom of the voucher.
                    </p>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Benefit Text
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      placeholder="e.g., on all items, on coffee only, on selected products"
                      value={voucherBenefitText}
                      onChange={(e) => setVoucherBenefitText(e.target.value)}
                    />
                  </div>
                  <div className="mt-4">
                    <ColorSwatchPicker
                      value={voucherColor}
                      onChange={setVoucherColor}
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expire Date
                    </label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={voucherExpireDate}
                      onChange={(e) => setVoucherExpireDate(e.target.value)}
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Voucher usage
                    </label>
                    <select
                      value={voucherSourceType}
                      onChange={(e) =>
                        setVoucherSourceType(
                          e.target.value === "LOTTERY" ? "LOTTERY" : "CHALLENGE"
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="CHALLENGE">Challenge reward</option>
                      <option value="LOTTERY">Lottery reward</option>
                    </select>
                  </div>
                  <div className="mt-4">
                    <Switch
                      crossOrigin={"anonymous"}
                      color="teal"
                      ripple
                      label="Allow multiple redeems"
                      checked={allowMultipleRedeems}
                      onChange={(e) =>
                        setAllowMultipleRedeems(e?.target.checked)
                      }
                    />
                  </div>
                </div>
                {/*footer*/}
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <RedButton
                    label="Cancel"
                    className="mr-4"
                    onClick={closeEditVoucherModal}
                  />
                  <PrimaryButton
                    disabled={!voucherCode.trim() || isVoucherSaving}
                    label={isVoucherSaving ? "Saving..." : "Update"}
                    onClick={updateExistingVoucher}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}

      {showBulkDataModal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-10/12 sm:w-1/4 my-0 sm:my-6 mx-auto max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className="flex items-start justify-center p-5 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-3xl font-semibold font-satoshi text-dark">
                    Vouchers Preview
                  </h3>
                </div>
                {/*body*/}
                <div className="relative p-6 flex-auto">
                  <div className="mb-4 p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-800 font-medium">
                      CSV/Excel Format: Code, Value, Description, Instructions,
                      Benefit Text, Expire Date
                    </p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Voucher usage for this upload
                    </label>
                    <select
                      value={voucherSourceType}
                      onChange={(e) =>
                        setVoucherSourceType(
                          e.target.value === "LOTTERY" ? "LOTTERY" : "CHALLENGE"
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="CHALLENGE">Challenge reward</option>
                      <option value="LOTTERY">Lottery reward</option>
                    </select>
                  </div>
                  {uploadedDataVouchers.map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-col border-b border-solid border-blueGray-200 py-3"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">{item.code}</p>
                        <p className="font-medium">
                          {item.value || "No value"}
                        </p>
                      </div>
                      {item.description && (
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Description:</span>{" "}
                          {item.description}
                        </p>
                      )}
                      {item.instructions && (
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Instructions:</span>{" "}
                          {item.instructions}
                        </p>
                      )}
                      {item.benefitText && (
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Benefit Text:</span>{" "}
                          {item.benefitText}
                        </p>
                      )}
                      {item.expireDate && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Expire Date:</span>{" "}
                          {new Date(item.expireDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                {/*footer*/}
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <RedButton
                    label="Cancel"
                    className="mr-4"
                    onClick={() => {
                      setShowBulkDataModal(false);
                      setUploadedDataVouchers([]);
                    }}
                  />
                  <PrimaryButton
                    disabled={false}
                    label="Upload"
                    onClick={() => {
                      addManyVoucher(uploadedDataVouchers);
                      setShowBulkDataModal(false);
                      setUploadedDataVouchers([]);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(EditPartnerScreen);
